import { get_company_modifiers } from '../../shared/action/shared_action';
import * as applied_modifiers_actions from '../action/applied_modifiers_action';
import { get_applied_modifiers_payer, get_applied_modifiers_insurance_class } from '../action/applied_modifiers_action';
import * as constants_actions from '../../admin/constants/action/constants_action';
import * as global_constants from '../../global_constants';
import * as AppliedModifiersConstants from '../constant/applied_modifiers_constants';
import { log_error } from '../../shared/action/shared_action';

/**
 * Interface for API client operations
 */
export interface IApiClient {
  getModifiersList(token: string, companyId: string): Promise<any>;
  getToChargeByList(token: string, companyId: string): Promise<any>;
  getPrimarySpecialtyList(token: string): Promise<any>;
  getPayerList(token: string): Promise<any>;
  getInsuranceClassList(token: string): Promise<any>;
}

/**
 * Interface for data service operations
 */
export interface IAppliedModifiersDataService {
  getModifiersList(companyId: string): Promise<any[]>;
  getToChargeByList(companyId: string): Promise<any[]>;
  getPrimarySpecialtyList(): Promise<any[]>;
  getPayerList(): Promise<any[]>;
  getInsuranceClassList(): Promise<any[]>;
}

/**
 * Default API client implementation
 */
export class DefaultApiClient implements IApiClient {
  constructor(private token: string) {}

  async getModifiersList(token: string, companyId: string): Promise<any> {
    return get_company_modifiers(token, companyId);
  }

  async getToChargeByList(token: string, companyId: string): Promise<any> {
    return applied_modifiers_actions.get_revenue_centers_and_proc_codes(token, companyId);
  }

  async getPrimarySpecialtyList(token: string): Promise<any> {
    const url = `${global_constants.end_points.provider_type_url.primary_speciality}`;
    return constants_actions.get_data(token, url);
  }

  async getPayerList(token: string): Promise<any> {
    return get_applied_modifiers_payer(token);
  }

  async getInsuranceClassList(token: string): Promise<any> {
    return get_applied_modifiers_insurance_class(token);
  }
}

/**
 * Data service for Applied Modifiers operations
 */
export class AppliedModifiersDataService implements IAppliedModifiersDataService {
  constructor(private apiClient: IApiClient, private token: string) {}

  async getModifiersList(companyId: string): Promise<any[]> {
    try {
      const response = await this.apiClient.getModifiersList(this.getToken(), companyId);
      if (response.data.data) {
        return this.updateDataModifier(response.data.data);
      } else {
        return this.updateDataModifier([]);
      }
    } catch (error) {
      log_error(error);
      return this.updateDataModifier([]);
    }
  }

  async getToChargeByList(companyId: string): Promise<any[]> {
    try {
      const response = await this.apiClient.getToChargeByList(this.getToken(), companyId);
      
      if (response && response.data && response.data.data) {
        // For new modifiers, remove all the procedure codes from tab 2 which label ends in "- INACTIVE"
        const filteredData = response.data.data.filter((row: any) => 
          !(row.tab === AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.tab
            && row.description.endsWith(AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.INACTIVE_SUFFIX))
        );

        return [{
          key: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab1.label,
          label: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab1.label,
          items: filteredData.filter((row: any) =>
            row.tab === AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab1.tab)
            .map(item => ({ value: item.id, label: item.description }))
        }, {
          key: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label,
          label: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label,
          items: filteredData.filter((row: any) =>
            row.tab === AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.tab)
            .map(item => ({ value: item.id, label: item.description }))
        }];
      } else {
        return [{
          key: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab1.label,
          label: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab1.label,
          items: []
        }, {
          key: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label,
          label: AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label,
          items: []
        }];
      }
    } catch (error) {
      log_error(error);
      return [];
    }
  }

  async getPrimarySpecialtyList(): Promise<any[]> {
    try {
      const response = await this.apiClient.getPrimarySpecialtyList(this.getToken());
      if (response.data.data) {
        return response.data.data.map((val: any, index: number) => ({ 
          value: val.specialty_id, 
          name: val.specialty_desc
        }));
      } else {
        return [];
      }
    } catch (error) {
      log_error(error);
      return [];
    }
  }

  async getPayerList(): Promise<any[]> {
    try {
      const response = await this.apiClient.getPayerList(this.getToken());
      if (response.data.data) {
        return response.data.data.map((item: any, index: number) => ({
          ...item,
          value: item.payer_id || `payer-${index}`,
          name: item.display_name || item.payer_description || item.payer_code || `Payer ${index + 1}`
        }));
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        log_error(error);
      }
      return [];
    }
  }

  async getInsuranceClassList(): Promise<any[]> {
    try {
      const response = await this.apiClient.getInsuranceClassList(this.getToken());
      if (response.data.data) {
        return response.data.data.map((item: any, index: number) => ({
          ...item,
          value: item.insurance_class_id || `insurance-class-${index}`,
          name: item.display_text || item.insurance_class_description || item.insurance_class_code || `Insurance Class ${index + 1}`
        }));
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        log_error(error);
      }
      return [];
    }
  }

  private getToken(): string {
    return this.token;
  }

  private updateDataModifier(source: any[]): any[] {
    const target: any[] = [];
    const source_length = source.length;

    if (source && source_length > 0) {
      for (let y = 0; y < source_length; y++) {
        let item = source[y];

        target.push({
          "text": (item.procedure_modifier_code ? item.procedure_modifier_code : AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.EMPTY_STRING) + AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.SPACE_SEPARATOR + (item.procedure_modifier_desc ? item.procedure_modifier_desc : AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.EMPTY_STRING),
          "value": item.id,
          "key": item.id,
          "code": item.procedure_modifier_code
        });
      }
    }

    return target;
  }
}
